import React from 'react';
import { Card, Row, Col, Statistic, Table, Progress, Tag, Spin, Alert, Empty } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExperimentOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Column } from '@ant-design/charts';
import { useQualityMetrics } from '../../../hooks/useManufacturingDashboard';

interface QualityMetricsPanelProps {
  period: 'today' | 'week' | 'month';
}

const QualityMetricsPanel: React.FC<QualityMetricsPanelProps> = ({ period }) => {
  const { data: quality, isLoading, error } = useQualityMetrics(period);

  if (error) {
    return (
      <Card title="Quality Metrics">
        <Alert
          message="Error Loading Quality Metrics"
          description={error.message}
          type="error"
          showIcon
        />
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card title="Quality Metrics">
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (!quality) {
    return (
      <Card title="Quality Metrics">
        <Empty description="No quality data available" />
      </Card>
    );
  }

  const defectColumns = [
    {
      title: 'Defect Reason',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
    },
    {
      title: 'Count',
      dataIndex: 'count',
      key: 'count',
      width: 80,
      render: (count: number) => <Tag color="red">{count}</Tag>,
    },
  ];

  // Trend data for chart
  const trendChartData = quality.inspectionTrend.flatMap(trend => [
    { date: trend.date, type: 'Passed', value: trend.passed },
    { date: trend.date, type: 'Failed', value: trend.failed },
  ]);

  const trendConfig: any = {
    data: trendChartData,
    xField: 'date',
    yField: 'value',
    seriesField: 'type',
    isGroup: true,
    columnStyle: {
      radius: [4, 4, 0, 0],
    },
    color: ({ type }: any) => type === 'Passed' ? '#52c41a' : '#ff4d4f',
    legend: {
      position: 'top' as const,
    },
    xAxis: {
      label: {
        autoHide: true,
        autoRotate: false,
      },
    },
  };

  return (
    <Card
      title="Quality Metrics"
      extra={<Tag>{period}</Tag>}
    >
      {/* Summary Stats */}
      <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Card size="small">
            <Statistic
              title="Total Inspections"
              value={quality.totalInspections}
              prefix={<ExperimentOutlined />}
              valueStyle={{ fontSize: 20 }}
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card size="small">
            <Statistic
              title="Pending"
              value={quality.pendingInspections}
              prefix={<WarningOutlined />}
              valueStyle={{ fontSize: 20, color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card size="small">
            <Statistic
              title="Passed"
              value={quality.passedInspections}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ fontSize: 20, color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card size="small">
            <Statistic
              title="Failed"
              value={quality.failedInspections}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ fontSize: 20, color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Pass Rate Gauge */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 8 }}>
          Pass Rate
        </div>
        <Progress
          percent={quality.passRate}
          strokeColor={{
            '0%': quality.passRate < 90 ? '#ff4d4f' : '#52c41a',
            '100%': quality.passRate < 90 ? '#ff7875' : '#389e0d',
          }}
          format={(percent) => `${percent?.toFixed(1)}%`}
        />
        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>
          {quality.passRate >= 95 ? '✓ Excellent' : 
           quality.passRate >= 90 ? '⚠️ Good' : '❌ Needs improvement'}
        </div>
      </div>

      {/* Defect Rate */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 8 }}>
          Defect Rate
        </div>
        <Progress
          percent={quality.defectRate}
          strokeColor="#ff4d4f"
          format={(percent) => `${percent?.toFixed(1)}%`}
        />
        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>
          {quality.defectRate < 5 ? '✓ Within limits' : '⚠️ Above threshold (5%)'}
        </div>
      </div>

      {/* Top Defect Reasons */}
      {quality.topDefectReasons && quality.topDefectReasons.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>
            Top Defect Reasons
          </div>
          <Table
            dataSource={quality.topDefectReasons}
            columns={defectColumns}
            rowKey="reason"
            size="small"
            pagination={false}
          />
        </div>
      )}

      {/* Inspection Trend Chart */}
      {quality.inspectionTrend && quality.inspectionTrend.length > 0 && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>
            7-Day Trend
          </div>
          <Column {...trendConfig} height={150} />
        </div>
      )}
    </Card>
  );
};

export default QualityMetricsPanel;
